import fetchWithAuth from "../utils/fetchWithAuth";
import { getEndpoints, buildEndpoint } from "../utils/ApiContext";

export const getCollections = async () => {
  try {
    const endpoints = await getEndpoints();
    const API_URL = await buildEndpoint("API_FETCH_USER_DOCUMENTS");
    
    const response = await fetchWithAuth(API_URL);
    
    if (!response.ok) throw new Error("Error al obtener documentos");
    const data = await response.json();
    
    if (data && Array.isArray(data.folders)) {
      const cleanCollections = data.folders.map(folder => folder.replace(/\/$/, ''));
      return cleanCollections;
    }
    
    if (data && Array.isArray(data.collections)) {
      return data.collections;
    }
    
    if (Array.isArray(data)) {
      return data;
    }
    
    return [];
  } catch (error) {
    throw error;
  }
};

export const validateProfileData = (profileData) => {
  if (!profileData.profile_id || typeof profileData.profile_id !== 'string') {
    throw new Error("El ID del perfil es requerido y debe ser un string");
  }

  if (!profileData.backend_config || typeof profileData.backend_config !== 'object') {
    throw new Error("La configuración del backend es requerida y debe ser un objeto");
  }

  if (!Array.isArray(profileData.backend_config.collections)) {
    throw new Error("Las colecciones deben ser un array");
  }
  
  if (profileData.backend_config.collections.length === 0) {
    throw new Error("Se requiere al menos una colección");
  }
  
  for (const collection of profileData.backend_config.collections) {
    if (typeof collection !== 'string' || !collection.trim()) {
      throw new Error("Las colecciones deben ser strings no vacíos");
    }
  }

  if (!Array.isArray(profileData.backend_config.prompt) || 
      profileData.backend_config.prompt.length === 0) {
    throw new Error("Se requiere al menos un prompt");
  }

  profileData.backend_config.prompt.forEach((prompt, index) => {
    if (!Array.isArray(prompt) || prompt.length !== 2 || 
        typeof prompt[0] !== 'string' || typeof prompt[1] !== 'string') {
      throw new Error(`El prompt en la posición ${index} tiene un formato inválido`);
    }
    if (!prompt[0].trim() || !prompt[1].trim()) {
      throw new Error(`El prompt en la posición ${index} contiene valores vacíos`);
    }
  });

  return true;
};

export const getProfiles = async () => {
  try {
    await getEndpoints();
    const API_URL = buildEndpoint('API_PROFILES');
    const response = await fetchWithAuth(API_URL);

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || `Error ${response.status}: ${response.statusText}`;
      } catch {
        errorMessage = `Error ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

// Admin-specific profile functions
export const createAdminProfile = async (profileData) => {
  try {
    validateProfileData(profileData);

    const url = buildEndpoint("API_ADMIN_PROFILES");
    
    const sanitizedData = {
      profile_id: profileData.profile_id.trim().toLowerCase(),
      backend_config: {
        collections: profileData.backend_config.collections,
        prompt: profileData.backend_config.prompt.map(([type, content]) => [
          type.split(' ')[0].trim().toLowerCase(),
          content.trim()
        ]),
        model_id: profileData.backend_config.model_id || "",
        temperature: profileData.backend_config.temperature !== undefined ? profileData.backend_config.temperature : null,
        num_search_documents: profileData.backend_config.num_search_documents !== undefined ? profileData.backend_config.num_search_documents : null,
        num_chat_messages: profileData.backend_config.num_chat_messages !== undefined ? profileData.backend_config.num_chat_messages : null
      }
    };



    const response = await fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      body: JSON.stringify(sanitizedData)
    });

    const responseText = await response.text();

    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = JSON.parse(responseText);
        errorMessage = errorData.message || `Error ${response.status}: ${response.statusText}`;
      } catch {
        errorMessage = `Error ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }
    
    if (!responseText || responseText.trim() === '') {
      return { success: true, profile_id: sanitizedData.profile_id };
    }

    try {
      const parsedResponse = JSON.parse(responseText);
      return parsedResponse;
    } catch (e) {
      return { success: true, profile_id: sanitizedData.profile_id };
    }
  } catch (error) {
    throw error;
  }
};

export const deleteAdminProfile = async (profileId) => {
  try {
    await getEndpoints();
    const baseUrl = buildEndpoint("API_ADMIN_PROFILES");
    const url = `${baseUrl}/${encodeURIComponent(profileId)}`;
    
    const resp = await fetchWithAuth(url, { method: "DELETE" });

    if (!resp.ok) {
      const txt = await resp.text();
      throw new Error(txt || `Error ${resp.status} al eliminar perfil`);
    }
    return true;
  } catch (err) {
    throw err;
  }
};

export const updateAdminProfile = async (profileId, profileData) => {
  try {
    validateProfileData(profileData);
    await getEndpoints();
    
    const baseUrl = buildEndpoint("API_ADMIN_PROFILES");
    const url = `${baseUrl}/${encodeURIComponent(profileId)}`;
    
    const sanitizedData = {
      ...profileData,
      profile_id: profileData.profile_id.trim().toLowerCase(),
      backend_config: {
        ...profileData.backend_config,
        prompt: profileData.backend_config.prompt.map(([type, content]) => [
          type.trim().toLowerCase(),
          content.trim()
        ])
      }
    };

    const resp = await fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(sanitizedData)
    });

    if (!resp.ok) {
      const txt = await resp.text();
      throw new Error(txt || `Error ${resp.status} al actualizar perfil`);
    }

    const responseText = await resp.text();

    if (!responseText || responseText.trim() === '') {
      return { success: true, profile_id: sanitizedData.profile_id };
    }

    try {
      return JSON.parse(responseText);
    } catch (e) {
      return { success: true, profile_id: sanitizedData.profile_id };
    }
  } catch (error) {
    throw error;
  }
};

export const createProfile = async (profileData) => {
  try {
    validateProfileData(profileData);

    await getEndpoints();
    const API_URL = buildEndpoint('API_PROFILES');    
    
    if (!API_URL) {
      throw new Error("URL de API para perfiles no disponible");
    }

    const sanitizedData = {
      profile_id: profileData.profile_id.trim().toLowerCase(),
      backend_config: {
        collections: profileData.backend_config.collections,
        prompt: profileData.backend_config.prompt.map(([type, content]) => [
          type.split(' ')[0].trim().toLowerCase(),
          content.trim()
        ])
      }
    };

    const response = await fetchWithAuth(API_URL, {
      method: "POST",
      headers: {
        "Accept": "application/json",
      },
      body: JSON.stringify(sanitizedData)
    });

    const responseText = await response.text();

    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = JSON.parse(responseText);
        errorMessage = errorData.message || `Error ${response.status}: ${response.statusText}`;
      } catch {
        errorMessage = `Error ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }
    
    if (!responseText || responseText.trim() === '') {
      return { success: true, profile_id: sanitizedData.profile_id };
    }

    try {
      const parsedResponse = JSON.parse(responseText);
      return parsedResponse;
    } catch (e) {
      return { success: true, profile_id: sanitizedData.profile_id };
    }
  } catch (error) {
    throw error;
  }
};

export const deleteProfile = async profileId => {
  try {
    await getEndpoints();
    const url  = buildEndpoint("API_PROFILES", profileId);
    const resp = await fetchWithAuth(url, { method: "DELETE" });

    if (!resp.ok) {
      const txt = await resp.text();
      throw new Error(txt || `Error ${resp.status} al eliminar perfil`);
    }
    return true;
  } catch (err) {
    throw err;
  }
};

export const updateProfile = async (profileId, profileData) => {
  try {
    validateProfileData(profileData);
    await getEndpoints();

    const url  = buildEndpoint("API_PROFILES", profileId);
    const sanitizedData = {
      ...profileData,
      profile_id: profileData.profile_id.trim().toLowerCase(),
      backend_config: {
        ...profileData.backend_config,
        prompt: profileData.backend_config.prompt.map(([type, content]) => [
          type.trim().toLowerCase(),
          content.trim()
        ])
      }
    };

    const resp = await fetchWithAuth(url, {
      method: "PUT",
      body: JSON.stringify(sanitizedData)
    });

    if (!resp.ok) {
      const txt = await resp.text();
      throw new Error(txt || `Error ${resp.status} al actualizar perfil`);
    }

    const responseText = await resp.text();

    if (!responseText || responseText.trim() === '') {
      return { success: true, profile_id: sanitizedData.profile_id };
    }

    try {
      return JSON.parse(responseText);
    } catch (e) {
      return { success: true, profile_id: sanitizedData.profile_id };
    }
  } catch (error) {
    throw error;
  }
};

export const getProfileById = async profileId => {
  try {
    await getEndpoints();
    const url  = buildEndpoint("API_PROFILES", profileId);
    const resp = await fetchWithAuth(url);

    if (!resp.ok) {
      const txt = await resp.text();
      throw new Error(txt || `Error ${resp.status} al obtener perfil`);
    }
    return resp.json();
  } catch (err) {
    throw err;
  }
};

export const getProfilesList = async (options = {}) => {
  try {
    await getEndpoints();
    const params = new URLSearchParams();
    
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.start_key) params.append('start_key', options.start_key);
    
    const API_URL = buildEndpoint('API_PROFILES') + (params.toString() ? `?${params.toString()}` : '');
    const response = await fetchWithAuth(API_URL);

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || `Error ${response.status}: ${response.statusText}`;
      } catch {
        errorMessage = `Error ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    
    if (data && Array.isArray(data.profiles)) {
      return {
        profiles: data.profiles,
        last_key: data.last_key || null
      };
    } else {
      throw new Error("La respuesta de la API no contiene una lista paginada de perfiles válida");
    }
  } catch (error) {
    throw error;
  }
};

export const getProfilesForSelector = async () => {
  try {
    const data = await getProfiles();
    
    if (data && Array.isArray(data.profiles)) {
      return data.profiles;
    }
    
    if (Array.isArray(data)) {
      return data;
    }
    
    return [];
  } catch (error) {
    throw error;
  }
};